import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { Divider, Form } from "antd";
import { FormInstance } from "antd/es/form";
import {
    ADDITIONAL_INFORMATION_FORM_ITEMS,
    CreateDownlineRequestFields,
    PERSONAL_INFORMATION_FORM_ITEMS,
} from "../../createDownline.constants";
import { SelectedProductCommission } from "../../createDownline.models";
import { Section } from "./components/section/section.index";
import { useContent } from "./content.hooks";
import styles from "./content.module.css";

interface ContentProps {
    form: FormInstance;
    onSubmit: (values: Record<string, string | number | SelectedProductCommission[]>) => void;
    selectedUserId: number | null;
    submitLoading: boolean;
}

export const Content = ({ form, onSubmit, selectedUserId, submitLoading }: ContentProps) => {
    const {
        agencyTypes,
        agencyTypeLoading,
        productsAndCommissions,
        productLoading,
        fetchAgencyTypes,
        fetchProductsAndCommissions,
        reset,
    } = useContent({ selectedUserId });

    const selectedProducts = Form.useWatch("products", form) as SelectedProductCommission[] | undefined;
    const selectedProductUid = Form.useWatch("selectedProductUid", form);
    const selectedCommissionUid = Form.useWatch("selectedCommissionUid", form);
    const selectedProductsList = selectedProducts ?? [];

    useEffect(() => {
        reset();
        form.setFieldsValue({
            [CreateDownlineRequestFields.USER_TYPE]: undefined,
            selectedProductUid: undefined,
            selectedCommissionUid: undefined,
            products: [],
        });
    }, [selectedUserId, form, reset]);

    const handleAgencyTypeOpen = async (open: boolean) => {
        if (!open || agencyTypes.length > 0) return;

        await fetchAgencyTypes();
    };

    const handleProductOpen = async (open: boolean) => {
        if (!open || productsAndCommissions.length > 0) return;

        await fetchProductsAndCommissions();
    };

    const agencyTypeOptions = useMemo(
        () =>
            agencyTypes.map((item) => ({
                label: item.userType,
                value: item.id,
            })),
        [agencyTypes]
    );

    const productOptions = useMemo(
        () =>
            productsAndCommissions.map((item) => ({
                label: item.productName,
                value: item.productUid,
                disabled: selectedProductsList.some((p) => p.productUid === item.productUid),
            })),
        [productsAndCommissions, selectedProductsList]
    );

    const commissionOptions = useMemo(() => {
        const product = productsAndCommissions.find((p) => p.productUid === selectedProductUid);
        if (!product) return [];

        return product.productCommissions.map((c) => ({
            label: c.commissionName,
            value: c.commissionUid,
        }));
    }, [productsAndCommissions, selectedProductUid]);

    const handleProductChange = () => {
        form.setFieldsValue({ selectedCommissionUid: undefined });
    };

    const handleAddProduct = () => {
        if (!selectedProductUid || !selectedCommissionUid) return;

        const product = productsAndCommissions.find((p) => p.productUid === selectedProductUid);
        const commission = product?.productCommissions.find((c) => c.commissionUid === selectedCommissionUid);

        if (!product || !commission) return;

        const next: SelectedProductCommission[] = [
            ...selectedProductsList,
            {
                productUid: product.productUid,
                productName: product.productName,
                commissionUid: commission.commissionUid,
                commissionName: commission.commissionName,
            },
        ];

        form.setFieldsValue({
            products: next,
            selectedProductUid: undefined,
            selectedCommissionUid: undefined,
        });

        form.setFields([{ name: "products", errors: [] }]);
    };

    const handleRemoveProduct = (productUid: string) => {
        const next = selectedProductsList.filter((p) => p.productUid !== productUid);
        form.setFieldsValue({ products: next });
    };

    const personalInformationFormItems = useMemo(
        () =>
            PERSONAL_INFORMATION_FORM_ITEMS.map((item) => {
                if (item.field !== CreateDownlineRequestFields.USER_TYPE) return item;

                return {
                    ...item,
                    options: agencyTypeOptions,
                    loading: agencyTypeLoading,
                    onOpenChange: handleAgencyTypeOpen,
                };
            }),
        [agencyTypeOptions, agencyTypeLoading]
    );

    const requiredMark = useCallback((labelNode: ReactNode, info: { required: boolean; }) => (
        <>
            {labelNode}
            {info.required && <span className={styles.requiredMark}>*</span>}
        </>
    ), []);

    return (
        <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
            scrollToFirstError
            className={styles.form}
            disabled={submitLoading}
            requiredMark={requiredMark}
        >
            <div className={styles.container}>
                <div className={styles.title}>Register User</div>
                <Divider className={styles.divider} />

                <div
                    className={`${styles.sectionsCtn} ${submitLoading ? styles.sectionsSaving : ""}`}
                    aria-busy={submitLoading}
                >
                    <Section
                        title="Personal Information"
                        formItems={personalInformationFormItems}
                        disabled={submitLoading}
                    />

                    <Section
                        title="Products and Commission Levels"
                        disabled={submitLoading}
                        productSectionConfig={{
                            selectedProducts: selectedProductsList,
                            productOptions,
                            commissionOptions,
                            selectedProductUid,
                            canAddProduct: Boolean(selectedProductUid && selectedCommissionUid),
                            productAndCommissionsLoading: productLoading,
                            onOpenProductDropdown: handleProductOpen,
                            onProductChange: handleProductChange,
                            onAddProduct: handleAddProduct,
                            onRemoveProduct: handleRemoveProduct,
                        }}
                    />

                    <Section
                        title="Additional Information"
                        formItems={ADDITIONAL_INFORMATION_FORM_ITEMS}
                        disabled={submitLoading}
                    />
                </div>
            </div>
        </Form>
    );
};
